import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import {
  Outbound,
  LoadingStatus,
  NgxIndexedDBService,
  OutboundObject,
} from "active-connect-ng2";
import { ClosedDay } from "../models/closed-day";
import { CostCalculation } from "../models/cost-calculation";
import { FeeGroup } from "../models/fee-group";
import { FeeGroupEntry } from "../models/fee-group-entry";
import { Library } from "../models/library";
import { LoginLog } from "../models/login-log";
import { Media } from "../models/media";
import { MediaClass } from "../models/media-class";
import { MediaLocation } from "../models/media-location";
import { MediaStatisticsCategory } from "../models/media-statistics-category";
import { MediaTaken } from "../models/media-taken";
import { MediaType } from "../models/media-type";
import { Notice } from "../models/notice";
import { OpeningHours } from "../models/opening-hours";
import { PublicFile } from "../models/public-file";
import { PublicLibrary } from "../models/public-library";
import { Reader } from "../models/reader";
import { ReaderClass } from "../models/reader-class";
import { ReaderStatisticsCategory } from "../models/reader-statistics-category";
import { Reservation } from "../models/reservation";
import { Roster } from "../models/roster";
import { SearchResult } from "../models/search-result";
import { Stocktake } from "../models/stocktake";
import { User } from "../models/user";
import { Weekday } from "../models/weekday";
import { environment } from "src/environments/environment";
import { NetworkLocation } from "../models/network-location";
import {
  LibraryNetworkRental,
  LibraryNetworkReservation,
} from "../public/search/medium";
import { MediaDetail } from "../models/media-detail";
import { Event } from "../models/event";
import { Article } from "../models/article";
import { FeeGroupReminderFee } from "../models/fee-group-reminder-fee";
import { SbTerminal } from "../models/sb-terminal";

function sortByStringProperty(prop: string, child: string | null = null) {
  if (child) {
    return function sort(a: any, b: any) {
      if (a && a[prop] && b && b[prop]) {
        if (a[prop][child] && a[prop][child].localeCompare) {
          return a[prop][child].localeCompare(b[prop][child]);
        } else if (b[prop][child] && b[prop][child].localeCompare) {
          return -b[prop][child].localeCompare(a[prop][child]);
        }
      }
      return 0;
    };
  }
  return function sort(a: any, b: any) {
    if (a && a[prop].localeCompare) {
      return a[prop].localeCompare(b[prop]);
    } else if (b && b[prop].localeCompare) {
      return -b[prop].localeCompare(a[prop]);
    }
    return 0;
  };
}
function sortById(desc?: boolean) {
  return function sort(a: any, b: any) {
    return ((a?.id || 0) - (b?.id || 0)) * (desc ? -1 : 1);
  };
}

@Injectable()
export class ApplicationPool extends LoadingStatus {
  get WssConnected(): boolean {
    return this.isConnected;
  }
  get data(): any {
    return (this as any).___data || (ApplicationPool.prototype as any).___data;
  }

  constructor(
    private router?: Router,
    dbService?: NgxIndexedDBService,
  ) {
    super(environment.WebSocket, false);
  }
  @Outbound("general.library", false, false, sortById())
  public GeneralLibraries!: Library[] | null;

  @Outbound("public.library")
  public SelectedLibrary!: PublicLibrary | null;

  @Outbound("rental.reader", false, false, sortById())
  public Readers!: Reader[] | null;

  public ExpiredReaders = new OutboundObject<Reader>(
    this,
    "rental.reader.expired",
    true,
  );

  @Outbound("rental.reader.feegroupmailstatus", true)
  public FeeGroupMailStatus!: any[] | null;

  @Outbound("auth.library")
  public Library!: Library | null;

  @Outbound("rental.mediatype", false, false, sortByStringProperty("desc"))
  public MediaTypes!: MediaType[] | null;

  @Outbound("rental.medialocation", false, false, sortByStringProperty("label"))
  public MediaLocations!: MediaLocation[] | null;

  @Outbound("rental.mediaclass", false, false, sortByStringProperty("sign"))
  public MediaClass!: MediaClass[] | null;

  @Outbound("rental.readerclass", false, false, sortByStringProperty("label"))
  public ReaderClass!: ReaderClass[] | null;

  @Outbound("rental.user", false, false, sortByStringProperty("username"))
  public Users!: User[] | null;

  @Outbound("rental.media", false, false, sortById())
  public Medias!: Media[] | null;

  public MediaDetails = new OutboundObject<MediaDetail>(
    this,
    "rental.media.detail",
    true,
    false,
    0,
    sortById(),
  );

  @Outbound("rental.media.flop", true, false, sortById())
  public MediaFlop!: Media[] | null;

  @Outbound("rental.taken.active", false, false, sortById(true))
  public ActiveMediaTaken!: MediaTaken[] | null;

  @Outbound("rental.reservation", false, false, sortById())
  public Reservations!: Reservation[] | null;

  @Outbound("auth.user")
  public User!: User | null;

  @Outbound("rental.feegroup", false, false, sortByStringProperty("label"))
  public FeeGroups!: FeeGroup[] | null;

  @Outbound("rental.feegroupentry", false, false)
  public FeeGroupEntries!: FeeGroupEntry[] | null;

  @Outbound("rental.feegroupreminderfee")
  public FeeGroupReminderFees!: FeeGroupReminderFee[] | null;

  public Stocktakes: OutboundObject<Stocktake> = new OutboundObject(
    this,
    "rental.stocktake",
    true,
    false,
    3,
    sortById(true),
  );

  @Outbound("public.openinghours")
  public OpeningHours!: OpeningHours[] | null;

  @Outbound("public.closedday", false, false, sortByStringProperty("startDate"))
  public ClosedDays!: ClosedDay[] | null;

  @Outbound("rental.roster", true, false, sortByStringProperty("selectedDate"))
  public Rosters!: Roster[] | null;

  @Outbound("admin.library", false, false, sortById())
  public AdminLibraries!: Library[] | null;

  @Outbound("rental.notice", true, false, sortById(true))
  public Notices!: Notice[] | null;

  @Outbound("rental.weekday", false, false)
  public Weekdays!: Weekday[] | null;

  @Outbound("rental.readerstatistic", false, false)
  public ReaderStatisticCategories!: ReaderStatisticsCategory[] | null;

  @Outbound("rental.mediastatistic", false, false)
  public MediaStatisticCategories!: MediaStatisticsCategory[] | null;

  @Outbound("rental.loginlog", true, false, sortById())
  public LoginLogs!: LoginLog[] | null;

  @Outbound("reader.reader")
  public SelectedReader!: Reader | null;

  @Outbound("reader.taken", false, false, sortById())
  public TakenMedia!: LibraryNetworkRental[] | null;

  @Outbound("reader.history", true, false, sortById(true))
  public History!: MediaTaken[] | null;

  @Outbound("reader.reservation", false, false, sortById())
  public ReaderReservations!: LibraryNetworkReservation[] | null;

  @Outbound("rental.notice.unread")
  public UnreadNotice!: Notice | null;

  @Outbound("rental.costcalculation", false, false)
  public CostCalculations!: CostCalculation[] | null;

  @Outbound("public.novelty")
  public NoveltyMedia!: SearchResult[] | null;

  @Outbound("public.file", false, false, sortById())
  public PublicFiles!: PublicFile[] | null;

  @Outbound("public.networklocation") public NetworkLocations!:
    | NetworkLocation[]
    | null;

  @Outbound("public.event")
  public PublicEvents!: Event[] | null;

  @Outbound("rental.event")
  public Events!: Event[] | null;

  @Outbound("public.article")
  public PublicArticles!: Article[] | null;

  @Outbound("rental.article", true)
  public Articles!: Article[] | null;

  @Outbound("rental.sb", true)
  public SbTerminals!: SbTerminal[] | null;

  // REGION SB
  @Outbound("sb.terminal")
  public Terminal!: SbTerminal;
  public TerminalReader = new OutboundObject<{
    id: number;
    name: string;
    birthdate: string;
    balance: number;
    rentals: {
      id: number;
      media: { id: number; title: string; author: string; thumb: string };
      latestReturnDate: string;
      allowExtension: boolean;
    }[];
  }>(this, "sb.reader", true);

  public get Symbols(): string[] {
    return [
      "gg-readme",
      "gg-album",
      "gg-awards",
      "gg-attachment",
      "gg-browser",
      "gg-calendar-due",
      "gg-copy",
      "gg-dice-3",
      "gg-file-document",
      "gg-film",
      "gg-folder",
      "gg-games",
      "gg-globe-alt",
      "gg-headset",
      "gg-heart",
      "gg-image",
      "gg-info",
      "gg-laptop",
      "gg-mic",
      "gg-music",
      "gg-play-button",
      "gg-vinyl",
      "gg-search",
    ];
  }

  public RosterNotificationOptions = [
    { id: -1, label: "Keine Benachrichtigung" },
    { id: 0, label: "Am Tag des Dienstes" },
    { id: 1, label: "1 Tag vor dem Dienst" },
    { id: 2, label: "2 Tage vor dem Dienst" },
    { id: 3, label: "3 Tage vor dem Dienst" },
    { id: 7, label: "1 Woche vor dem Dienst" },
  ];

  public clearVarsAfterLogout() {
    this.AdminLibraries = null;
    this.ActiveMediaTaken = null;
    this.Library = null;
    this.MediaClass = null;
    this.Medias = null;
    this.MediaTypes = null;
    this.ReaderClass = null;
    this.Readers = null;
    this.Reservations = null;
    this.User = null;
    this.Users = null;
    this.FeeGroupEntries = null;
    this.FeeGroups = null;
    this.LoginLogs = null;
    this.MediaStatisticCategories = null;
    this.Notices = null;
    this.ReaderStatisticCategories = null;
    this.Rosters = null;
    this.SelectedReader = null;
    this.TakenMedia = null;
    this.ReaderReservations = null;
    this.UnreadNotice = null;
    this.CostCalculations = null;
  }
}
